import React, { useState, useEffect } from 'react';
import { Button } from '@shopify/polaris';
import './SubscriptionModal.css';

function getShopDomain() {
  const params = new URLSearchParams(window.location.search);
  return params.get('shop');
}

const SubscriptionModal = ({ isOpen, onClose, currentPlan, error, onUpgradeSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('pro');
  const [shopifyLoading, setShopifyLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errorModal, setErrorModal] = useState(null);

  const plans = {
    pro: {
      name: 'Pro',
      price: '$20',
      features: [
        '50 queries per day',
        'Priority support',
        'Query history',
        'Export to CSV'
      ]
    },
    unlimited: {
      name: 'Unlimited',
      price: '$100',
      features: [
        'Unlimited queries',
        '24/7 support',
        'Query history',
        'Export to CSV',
        'Custom integrations',
        'Dedicated account manager'
      ]
    }
  };

  const planKeyMap = {
    pro: 20,
    unlimited: 100
  };

  const shopDomain = getShopDomain();
  const appSlug = 'askeet';
  const adminAppUrl = shopDomain ? `https://admin.shopify.com/store/${shopDomain.replace('.myshopify.com','')}/apps/${appSlug}` : window.location.origin;

  // Helper to get install_id
  const INSTALL_ID = window.install_id || shopDomain;

  // Smart plan logic
  const isPro = currentPlan === 'premium' || currentPlan === '20';
  const isFree = !currentPlan || currentPlan === 'free';
  const isPaid = currentPlan === '20' || currentPlan === '100';
  const planName = currentPlan === '20' ? 'Pro' : currentPlan === '100' ? 'Unlimited' : 'Free';

  // Helper to detect if Shopify context (shop domain present)
  const isShopify = !!shopDomain;

  const handleUpgrade = async () => {
    setLoading(true);
    setErrorModal(null);
    try {
      if (isPro) {
        // Only allow upgrade to unlimited
        const response = await fetch('/api/upgrade-subscription', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ install_id: INSTALL_ID, plan: '100' })
        });
        const data = await response.json();
        setLoading(false);
        if (data.success) {
          onClose(); // Close subscription modal first
          if (onUpgradeSuccess) onUpgradeSuccess();
        } else {
          setErrorModal(data.error || 'Failed to upgrade subscription.');
        }
        return;
      }
      // Free: allow checkout for selected plan
      const selectedPlanKey = planKeyMap[selectedPlan];
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan: selectedPlanKey,
          install_id: INSTALL_ID,
          site_url: window.location.origin,
          success_url: adminAppUrl,
          cancel_url: adminAppUrl
        }),
      });
      const data = await response.json();
      setLoading(false);
      if (data.checkout_url) {
        if (window.top !== window.self) {
          window.top.location.href = data.checkout_url;
        } else {
          window.location.href = data.checkout_url;
        }
      } else {
        setErrorModal(data.error || 'Failed to create checkout session');
      }
    } catch (err) {
      setLoading(false);
      setErrorModal('Failed to upgrade subscription. Please try again.');
    }
  };

  // Shopify Billing handler
  const handleShopifyBilling = async () => {
    setShopifyLoading(true);
    try {
      const plan = isPro ? 'unlimited' : selectedPlan;
      const response = await fetch('/api/shopify-billing/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop: shopDomain, plan, success_url: adminAppUrl, cancel_url: adminAppUrl }),
      });
      const data = await response.json();
      setShopifyLoading(false);
      if (data.confirmation_url) {
        window.top.location.href = data.confirmation_url;
      } else {
        alert(data.error || 'Failed to start Shopify billing.');
      }
    } catch (err) {
      setShopifyLoading(false);
      alert('Failed to start Shopify billing.');
    }
  };

  // Add this effect to detect charge_id and activate billing
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const chargeId = params.get('charge_id');
    const shop = params.get('shop');
    if (chargeId && shop) {
      fetch('/api/shopify-billing/activate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop, charge_id: chargeId }),
      })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setShowSuccessModal(true);
        }
      });
    }
  }, []);

  const handleManageSubscription = async () => {
    setLoading(true);
    setErrorModal(null);
    try {
      const response = await fetch('/api/stripe-portal-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ install_id: INSTALL_ID, return_url: adminAppUrl })
      });
      const data = await response.json();
      setLoading(false);
      if (data.portalUrl) {
        window.open(data.portalUrl, '_blank');
      } else {
        setErrorModal(data.error || 'Failed to open Stripe portal.');
      }
    } catch (err) {
      setLoading(false);
      setErrorModal('Failed to open Stripe portal. Please try again.');
    }
  };

  if (!isOpen && !showSuccessModal) return null;

  return (
    <>
      {isOpen && (
        <div className="subscription-modal-overlay">
          <div className="subscription-modal">
            <button className="close-button" onClick={onClose}>×</button>
            <h2>Upgrade Your Plan</h2>
            {error && <div className="error-message">{error}</div>}
            {currentPlan && (
              <div className="current-plan">
                Current Plan: <span>{planName}</span>
              </div>
            )}
            <div className="plans-container">
              {isFree && Object.entries(plans).map(([key, plan]) => (
                <div
                  key={key}
                  className={`plan-card ${selectedPlan === key ? 'selected' : ''}`}
                  onClick={() => setSelectedPlan(key)}
                >
                  <h3>{plan.name}</h3>
                  <div className="price">{plan.price}/month</div>
                  <ul>
                    {plan.features.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                </div>
              ))}
              {isPro && (
                <div
                  className={`plan-card selected`}
                  style={{ cursor: 'default', border: '2px solid #0070f3' }}
                >
                  <h3>{plans.unlimited.name}</h3>
                  <div className="price">{plans.unlimited.price}/month</div>
                  <ul>
                    {plans.unlimited.features.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <button
              className="upgrade-button"
              onClick={handleUpgrade}
              disabled={loading}
            >
              {loading ? 'Processing...' : isPro ? 'Upgrade to Unlimited (Stripe)' : 'Upgrade Now (Stripe)'}
            </button>
            {isShopify && (
              <button
                className="upgrade-button"
                style={{ background: '#fff', color: '#005a9e', border: '2px solid #005a9e' }}
                onClick={handleShopifyBilling}
                disabled={shopifyLoading}
              >
                {shopifyLoading ? 'Redirecting...' : isPro ? 'Upgrade to Unlimited (Shopify)' : 'Upgrade Now (Shopify)'}
              </button>
            )}
          </div>
        </div>
      )}
      {showSuccessModal && (
        <div className="subscription-modal-overlay">
          <div className="subscription-modal success-modal modern-success-modal">
            <button className="close-button" onClick={() => {
              setShowSuccessModal(false);
              window.top.location.href = adminAppUrl;
            }}>×</button>
            <div className="success-icon" aria-label="Celebration" role="img">🎉</div>
            <div className="success-title">Subscription Activated!</div>
            <p className="success-message">
              Your Shopify subscription is now <b>active</b>.<br />
              Thank you!
            </p>
            <button className="upgrade-button" onClick={() => {
              setShowSuccessModal(false);
              window.top.location.href = adminAppUrl;
            }}>
              Go to App
            </button>
          </div>
        </div>
      )}
      {errorModal && (
        <div className="subscription-modal-overlay">
          <div className="subscription-modal error-modal modern-error-modal">
            <button className="close-button" onClick={() => setErrorModal(null)}>×</button>
            <div className="error-icon" aria-label="Error" role="img">❌</div>
            <div className="error-title">Upgrade Failed</div>
            <p className="error-message">{errorModal}</p>
            <button className="upgrade-button" onClick={() => setErrorModal(null)}>
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default SubscriptionModal; 